import {TSector} from '../types/sector.type'

/** Which source owns the linear-view window (mutual exclusion guarantees one). */
export type TWindowSource = 'manualPk' | 'sector' | 'extent'

export type TPkEnvelope = {kmStart: number; kmEnd: number}

/**
 * A window override: the bounds a user typed by hand, plus the ruler
 * granularity. All optional — each field, when unset, falls through to the
 * sector envelope then the network extent (see `resolveWindowBounds`).
 */
export type TWindowOverride = {
  pkStart?: number
  pkEnd?: number
  granularity?: number
}

export type TResolvedBounds = {
  pkStart: number | undefined
  pkEnd: number | undefined
  granularity: number
}

/**
 * The active window, discriminated by source: a pk-mode result carries
 * explicit bounds, a sector-mode result carries the selected sector ids so
 * the caller can route to the sector endpoints instead of a front-computed
 * hull.
 */
export type TActiveWindow =
  | {
      mode: 'pk'
      start: number | undefined
      end: number | undefined
      /** `undefined` = let the axis builder use its default (off the work tab). */
      granularity: number | undefined
      /** Both bounds present and finite — safe to fetch the viewport. */
      boundsValid: boolean
    }
  | {
      mode: 'sector'
      sectorIds: string[]
      granularity: number | undefined
      /** At least one sector id — safe to fetch by sector. */
      boundsValid: boolean
    }

/**
 * The active window the shared viewport/axis should actually use.
 *
 * The window is a work-view control, but the data wrapper that fetches the
 * viewport is mounted on every tab and feeds SHARED state (axis, bands,
 * context tracks). So the window only applies when the work tab is active;
 * everywhere else the bounds fall back to the full network extent (always
 * pk-mode) — otherwise a sector picked to filter another tab's list would
 * silently narrow the viewport and drop lanes app-wide.
 *
 * IMPORTANT — `extent` must be the RAW referential extent, never the
 * baseline from `resolveBaselineEnvelope`: the network-configured default
 * window rides through `window` (the resolved bounds) and applies only when
 * `windowActive`. Feeding the baseline here would narrow the shared
 * axis/bands on non-work tabs and silently hide activities from the
 * activities list.
 */
export const pickActiveWindow = (
  windowActive: boolean,
  window: TResolvedBounds & {
    boundsAreValid: boolean
    source: TWindowSource
    sectorIds: string[]
  },
  extent: TPkEnvelope | null,
): TActiveWindow => {
  if (windowActive && window.source === 'sector') {
    return {
      mode: 'sector',
      sectorIds: window.sectorIds,
      granularity: window.granularity,
      boundsValid: window.sectorIds.length > 0,
    }
  }
  if (windowActive) {
    return {
      mode: 'pk',
      start: window.pkStart,
      end: window.pkEnd,
      granularity: window.granularity,
      boundsValid: window.boundsAreValid,
    }
  }
  const start = extent?.kmStart
  const end = extent?.kmEnd
  return {
    mode: 'pk',
    start,
    end,
    granularity: undefined,
    boundsValid:
      start !== undefined &&
      end !== undefined &&
      Number.isFinite(start) &&
      Number.isFinite(end),
  }
}

/**
 * PK envelope of the selected sectors: the outer hull (min start / max end)
 * over every segment of every selected sector.
 *
 * A sector can span several projects/blocks, so its segments are not
 * contiguous — the hull is deliberate: it matches the multi-block window the
 * linear axis already draws from a single `pkStart → pkEnd`.
 *
 * Returns `null` when nothing is selected, or when the selection carries no
 * segment; the caller then falls back to the network's referential extent.
 */
export const getSelectedSectorsEnvelope = (
  sectors: TSector[],
  selectedSectorIds: string[],
): TPkEnvelope | null => {
  if (selectedSectorIds.length === 0) return null
  const selected = new Set(selectedSectorIds)
  const bounds = sectors
    .filter(sector => selected.has(sector.id))
    .flatMap(sector =>
      sector.segments.flatMap(segment => [segment.kmStart, segment.kmEnd]),
    )
  if (bounds.length === 0) return null
  return {kmStart: Math.min(...bounds), kmEnd: Math.max(...bounds)}
}

/**
 * The selected sector ids that still exist on the current network.
 *
 * Sector selection is persisted globally (and survives a reload), so a
 * selection made on one network — or on a since-reset demo network whose ids
 * were regenerated — can reach a network where those ids no longer exist. Left
 * unfiltered it puts the window in sector mode with an id that resolves to an
 * empty viewport, and the linear view hangs forever on a null axis. Pruning
 * against the loaded catalogue makes a stale-only selection fall back to the
 * extent, mirroring how `selectedTrack`/`workTrackIds` already self-heal.
 *
 * While the catalogue is still loading (`sectors === undefined`) the raw
 * selection is kept: a not-yet-loaded list must not be mistaken for a stale one.
 */
export const effectiveSelectedSectorIds = (
  selectedSectorIds: string[],
  sectors: TSector[] | undefined,
): string[] => {
  if (!sectors) return selectedSectorIds
  const known = new Set(sectors.map(sector => sector.id))
  return selectedSectorIds.filter(id => known.has(id))
}

/**
 * The baseline window of the linear views: the network-configured default PK
 * range when one exists, otherwise the referential extent.
 */
export const resolveBaselineEnvelope = (
  networkDefault: TPkEnvelope | null,
  extent: TPkEnvelope | null,
): TPkEnvelope | null => {
  if (!networkDefault || !extent) return extent
  const clamp = (value: number) =>
    Math.min(Math.max(value, extent.kmStart), extent.kmEnd)
  const kmStart = clamp(networkDefault.kmStart)
  const kmEnd = clamp(networkDefault.kmEnd)
  if (kmStart === kmEnd) return extent
  return {kmStart, kmEnd}
}

/**
 * Resolves the effective window bounds by precedence:
 * manual `override` → sector envelope → network extent.
 *
 * Any field left unset falls through to the next source, independently — a
 * user who typed only a start keeps the sector/extent end.
 */
export const resolveWindowBounds = (
  override: TWindowOverride,
  sectorEnvelope: TPkEnvelope | null,
  extent: TPkEnvelope | null,
  defaultGranularity: number,
): TResolvedBounds => ({
  pkStart: override.pkStart ?? sectorEnvelope?.kmStart ?? extent?.kmStart,
  pkEnd: override.pkEnd ?? sectorEnvelope?.kmEnd ?? extent?.kmEnd,
  granularity: override.granularity ?? defaultGranularity,
})

/**
 * Drops the hand-typed PK bounds while keeping the ruler granularity — applied
 * when the sector selection changes, so each sector pick sets the window afresh
 * (and clearing the selection reverts to the extent) without disturbing the
 * chosen display density.
 */
export const clearManualBounds = (
  override: TWindowOverride,
): TWindowOverride =>
  override.granularity === undefined ? {} : {granularity: override.granularity}
